"use client";

import { Globe, Check } from "lucide-react";
import { useLocale } from "next-intl";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { usePathname, useRouter } from "next/navigation";
import Cookies from "js-cookie";

export default function LanguageSwitcher() {
  const active = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const isDashboard = pathname.includes("/dashboard") || pathname.includes("/bank");

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const languages = [
    { code: "en", label: "English" },
    { code: "ar", label: "العربية" },
  ];

  const handleChangeLang = (nextLocale: "ar" | "en") => {
    Cookies.set("SNA_DEVELOPER_NEXT_LOCALE", nextLocale, {
      path: "/",
      expires: 30,
    });

    const currentLocale = pathname.split("/")[1];
    const cleanPath = pathname.startsWith(`/${currentLocale}`)
      ? pathname.slice(currentLocale.length + 1)
      : pathname;

    const searchParams = new URLSearchParams(window.location.search);

    const newPath =
      cleanPath === ""
        ? `/${nextLocale}?${searchParams.toString()}`
        : `/${nextLocale}/${cleanPath}?${searchParams.toString()}`;

    router.push(newPath);

    setOpen(false);
  };

  const handleSelect = (code: "ar" | "en") => {
    handleChangeLang(code);
  };

  return (
    <div ref={menuRef} className="relative select-none">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2  backdrop-blur-md border px-4 py-2 rounded-full hover:bg-white/20 transition-all ${
          isDashboard
            ? "border-black text-black"
            : "bg-white/10 border-white/30 text-white"
        }`}
      >
        <Globe className="w-5 h-5" />
        <span className="text-sm font-medium">
          {active === "ar" ? "العربية" : "English"}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full mt-2 w-40 bg-white/90 backdrop-blur-xl border border-gray-100 rounded-2xl shadow-lg overflow-hidden z-50"
          >
            {languages.map((lang) => (
              <li
                key={lang.code}
                onClick={() => handleSelect(lang.code as "ar" | "en")}
                className={`flex items-center justify-between px-4 py-3 text-sm font-medium cursor-pointer transition-all duration-200 text-black`}
              >
                <span>{lang.label}</span>
                {active === lang.code && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                  >
                    <Check className="w-4 h-4 text-main" />
                  </motion.div>
                )}
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

// SNA_DEVELOPER_NEXT_LOCALE
